import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Clock } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { resolvePlaybookIcon } from '@/components/shopify/playbookIcons';
import {
  WOOCOMMERCE_AUTO_MESSAGES,
  WooCommerceAutoMessageCatalogEntry,
} from './wooCommerceAutoMessagesCatalog';

/**
 * WooCommerce Auto Messages — UI-only preview.
 *
 * Renders the same card grid as Shopify Auto Messages but against a
 * frontend-only catalog. Enable is intentionally stubbed with a "coming
 * soon" toast because the backend half (webhook intake, connector-level
 * contact auto-create, catalog/service/routes, flow generator) hasn't
 * been wired yet. When the backend lands, this component should shift to
 * reusing `PlaybookDialog` against a `wooCommerceAutoMessagesApi` client —
 * all the dialog / preview / variable-library plumbing is already generic
 * enough to be shared. See
 * [web/src/components/shopify/ShopifyPlaybooks.tsx] for the functional
 * reference implementation.
 */

interface WooCommerceAutoMessagesProps {
  businessAccountId: string;
  isConnected: boolean;
}

function handleComingSoon(entry: WooCommerceAutoMessageCatalogEntry) {
  toast({
    title: 'Coming soon',
    description: `${entry.title} will fire the moment the WooCommerce webhook pipeline ships. The catalog + template picker are already designed — just waiting on backend wiring.`,
  });
}

export const WooCommerceAutoMessages: React.FC<WooCommerceAutoMessagesProps> = ({
  isConnected,
}) => {
  if (!isConnected) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          Connect a WooCommerce store on the Overview tab to enable auto messages.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="woocommerce-auto-messages">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold">Auto Messages</h2>
          <p className="text-sm text-muted-foreground max-w-3xl">
            Pick an approved WhatsApp template for each WooCommerce event. We'll wire the
            automation flow for you — no drag-drop needed. Preview only right now; the
            webhook pipeline that fires these messages in real time is being built next.
          </p>
        </div>
        <Badge variant="outline" className="gap-1.5 border-amber-400 text-amber-700">
          <Clock className="h-3 w-3" />
          Preview — backend pending
        </Badge>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {WOOCOMMERCE_AUTO_MESSAGES.map((entry) => {
          const Icon = resolvePlaybookIcon(entry.icon);
          return (
            <Card
              key={entry.key}
              data-testid={`woocommerce-auto-message-card-${entry.key}`}
              className="border-border opacity-95"
            >
              <CardHeader className="pb-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-muted text-muted-foreground flex items-center justify-center flex-shrink-0">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-base flex items-center gap-2">
                      <span className="truncate">{entry.title}</span>
                    </CardTitle>
                    <CardDescription className="text-xs mt-1">
                      {entry.description}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-3">
                <div className="text-xs text-muted-foreground">
                  Event: <code className="font-mono text-[11px]">{entry.triggerType}</code>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-dashed"
                  onClick={() => handleComingSoon(entry)}
                  data-testid={`woocommerce-auto-message-enable-${entry.key}`}
                >
                  Enable (coming soon)
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default WooCommerceAutoMessages;
